import { useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";
import { useSetAtom } from "jotai";

import { useCompositionsList } from "@design-lab/entities/composition";
import { useApplyStandardPlacement } from "@design-lab/shared/lib/config";
import { trackStandardPlacementFallthrough } from "@design-lab/shared/lib/analytics";
import type { ArtworkForm, PlacementFormPosition } from "@design-lab/shared/types";

import { resolveStandardPlacementResize } from "../lib/resolveStandardPlacementResize";
import type { ArtworkSize } from "../lib/fitArtworkToStandard";
import { standardPlacementResizeActiveAtom } from "../lib/standardPlacementState";
import { useArtworkForm } from "./useArtworkForm";

const PLACEMENTS: PlacementFormPosition[] = ["front", "frontLeftChest", "back"];

/**
 * MN-8890 — flag-gated per-garment-size standard placement auto-resize.
 *
 * When the `print_size_standard_placement` flip is ON and every garment size IN THE WHOLE ORDER
 * (across all compositions) shares one standard for a placement, this resizes that placement's
 * artwork to the standard, cap-not-target + aspect-preserving. Genuinely mixed-size orders defer to
 * the MN-9933 per-line-item backend; unmapped sizes fail safe to legacy/manual and emit a metric.
 *
 * Correctness guards (flag-ON review feedback):
 *  - Order-level sizes: collects sizes across ALL compositions, so a multi-composition mixed order
 *    falls through instead of resizing to one composition's size.
 *  - Apply-once per (placement, artwork, size-set): the cap is applied once, so dragging artwork
 *    larger than the standard is NOT snapped back (no rubber-banding).
 *  - Restore-on-fallthrough: if a placement was auto-resized and the order later becomes
 *    mixed/unmapped, restore the pre-resize size (only if our auto value is still in place, so a
 *    manual edit isn't clobbered).
 *
 * Strict no-op when the flag is OFF; the legacy surface sizing then stands.
 */
export function usePrintStandardPlacementResize() {
  const enabled = useApplyStandardPlacement();
  const { form } = useArtworkForm();
  const { compositions, formCompositions } = useCompositionsList();
  const setResizeActive = useSetAtom(standardPlacementResizeActiveAtom);

  const artworkForm = useWatch({ control: form.control }) as ArtworkForm | undefined;

  // The (placement, artwork, size-set) combo we last applied a resize for — so we apply once and
  // don't fight the user's drag-resize. Cleared on restore so a later identical order re-applies.
  const lastComboRef = useRef<Map<PlacementFormPosition, string>>(new Map());
  // The ORIGINAL (pre-resize) artwork size per placement, keyed by artwork. Re-fits always start
  // from here so switching from a smaller standard to a larger one grows back, never stays capped.
  const baselineRef = useRef<Map<PlacementFormPosition, { artworkId: string; size: ArtworkSize }>>(
    new Map()
  );
  const lastAppliedRef = useRef<Map<PlacementFormPosition, ArtworkSize>>(new Map());
  const reportedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || !artworkForm) {
      setResizeActive(false);
      return;
    }

    // Order-level selected garment sizes across EVERY composition (not just the current one).
    let hasUnresolvedSelected = false;
    const selectedSizes = formCompositions.flatMap(fc => {
      const quantities = fc.variants ?? {};
      const selectedIds = Object.keys(quantities).filter(id => (quantities[id] ?? 0) > 0);
      const catalog = compositions.find(c => c.id === fc.id);
      if (!catalog) {
        // A selected composition whose catalog hasn't loaded yet (pending/failed product query)
        // would silently drop its sizes and make a mixed order look homogeneous — fail safe.
        if (selectedIds.length > 0) hasUnresolvedSelected = true;
        return [] as string[];
      }
      const catalogVariants = catalog.variants ?? [];
      // A positive-qty variant id absent from the resolved catalog (e.g. a discontinued size the
      // serializer omits) would also be dropped silently — fail safe rather than mis-resize.
      const catalogIds = new Set(catalogVariants.map(v => v.id));
      if (selectedIds.some(id => !catalogIds.has(id))) hasUnresolvedSelected = true;
      return catalogVariants.filter(variant => (quantities[variant.id] ?? 0) > 0).map(v => v.size);
    });

    // Fail safe: a selected composition/variant we can't resolve, OR no sizes selected at all (which
    // in the SizeSelector flow is a transient step when switching sizes, e.g. S->0 then 2XL->1).
    // Restore any already-applied resize to its pre-resize size so Add to Cart never submits a stale
    // capped size and a later selection re-fits from the original, then clear the bookkeeping.
    if (hasUnresolvedSelected || selectedSizes.length === 0) {
      PLACEMENTS.forEach(placement => {
        const lastApplied = lastAppliedRef.current.get(placement);
        const currentSize = artworkForm[placement]?.size;
        if (
          lastApplied &&
          currentSize &&
          currentSize.width === lastApplied.width &&
          currentSize.height === lastApplied.height
        ) {
          const baseline = baselineRef.current.get(placement);
          if (baseline) form.setValue(`${placement}.size`, baseline.size);
        }
        lastComboRef.current.delete(placement);
        lastAppliedRef.current.delete(placement);
        baselineRef.current.delete(placement);
      });
      setResizeActive(false);
      return;
    }
    let anyResizeActive = false;
    let anyFellThrough = false;

    // Only the submitted placements matter — uploads populate both front-side records but
    // useAddToCartSubmit sends back + the selectedFrontPlacement. Resizing/activating the hidden
    // front-side placement would show the notice for art that never reaches the cart.
    const { selectedFrontPlacement } = artworkForm;
    const submittedPlacements = PLACEMENTS.filter(
      placement => placement === "back" || selectedFrontPlacement === placement
    );

    submittedPlacements.forEach(placement => {
      const placementData = artworkForm[placement];
      const currentSize = placementData?.size;
      const artworkId = placementData?.artworkId;
      if (!artworkId || !currentSize?.width || !currentSize?.height) return;

      // The artwork was replaced since we last touched this placement → the old artwork's baseline /
      // last-applied no longer apply (a later restore would put the old art's dims on the new one).
      // Drop them regardless of whether the new artwork needs a resize.
      const existingBaseline = baselineRef.current.get(placement);
      if (existingBaseline && existingBaseline.artworkId !== artworkId) {
        baselineRef.current.delete(placement);
        lastComboRef.current.delete(placement);
        lastAppliedRef.current.delete(placement);
      }

      // Re-fit from the original artwork size (per artwork), not the already-capped current size.
      const baselineEntry = baselineRef.current.get(placement);
      const baseline =
        baselineEntry && baselineEntry.artworkId === artworkId ? baselineEntry.size : currentSize;

      const result = resolveStandardPlacementResize({
        placement,
        selectedSizes,
        currentSize: baseline
      });

      if (result.kind === "resize") {
        // Key the apply-once guard by the RESOLVED STANDARD bucket, not the fitted result size:
        // adult M and L share one front standard (so switching them mustn't clobber a manual edit),
        // and art that already fits records the same key as art that needed capping (so a later drag
        // past the cap isn't re-resized). Same artwork + same standard ⇒ same key.
        const comboKey = `${artworkId}|${result.standard.widthInches}x${result.standard.heightInches}`;
        // Already applied for this exact combo → leave the user's size (don't snap back a drag).
        if (lastComboRef.current.get(placement) === comboKey) {
          // The notice stays on only while our auto-applied value is still in place; once the
          // customer manually drags to a different size the resize is no longer in effect.
          const lastApplied = lastAppliedRef.current.get(placement);
          if (
            lastApplied &&
            currentSize.width === lastApplied.width &&
            currentSize.height === lastApplied.height
          ) {
            anyResizeActive = true;
          }
          return;
        }
        lastComboRef.current.set(placement, comboKey);
        // Only resize / record bookkeeping when the standard actually constrains the art; if it
        // already fits there is nothing to change and the notice stays hidden.
        if (result.size.width !== currentSize.width || result.size.height !== currentSize.height) {
          anyResizeActive = true;
          if (!baselineEntry || baselineEntry.artworkId !== artworkId) {
            baselineRef.current.set(placement, { artworkId, size: currentSize });
          }
          form.setValue(`${placement}.size`, result.size);
          lastAppliedRef.current.set(placement, result.size);
        }
        return;
      }

      if (result.reason === "no-sizes") return;

      // A submitted placement with artwork was deferred (mixed/unmapped) — its art is NOT resized
      // per-size, so the "resized for each garment size" notice would over-promise.
      anyFellThrough = true;

      // Fallthrough (mixed/unmapped): if our auto value is still in place, restore the pre-resize
      // size (don't clobber a manual edit), then allow a later order to re-apply.
      const lastApplied = lastAppliedRef.current.get(placement);
      if (
        lastApplied &&
        currentSize.width === lastApplied.width &&
        currentSize.height === lastApplied.height
      ) {
        const restoreTo = baselineRef.current.get(placement);
        if (restoreTo) form.setValue(`${placement}.size`, restoreTo.size);
      }
      lastComboRef.current.delete(placement);
      lastAppliedRef.current.delete(placement);
      baselineRef.current.delete(placement);

      const sizes = result.reason === "unmapped" ? result.unmappedSizes : selectedSizes;
      const metricKey = `${placement}-${result.reason}-${sizes.join(",")}`;
      if (!reportedRef.current.has(metricKey)) {
        reportedRef.current.add(metricKey);
        trackStandardPlacementFallthrough({ placement, reason: result.reason, sizes });
      }
    });

    // Only promise "resized for each garment size" when EVERY submitted placement with artwork was
    // resized — if any fell through (mixed/unmapped), the notice would over-promise.
    setResizeActive(anyResizeActive && !anyFellThrough);
  }, [enabled, artworkForm, compositions, formCompositions, form, setResizeActive]);
}
